import { useState, useEffect, useRef, useCallback } from "react";
import API from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  MapPin, Navigation, Gauge, Route, DollarSign, ExternalLink, Locate,
  Shield, TrendingDown, Zap, AlertTriangle
} from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const customerIcon = new L.DivIcon({
  className: "custom-marker",
  html: `<div style="width:32px;height:32px;border-radius:50%;background:#dc2626;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3" fill="#dc2626"/></svg>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
});

const techIcon = new L.DivIcon({
  className: "custom-marker",
  html: `<div style="width:28px;height:28px;border-radius:50%;background:#16a34a;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="white"><circle cx="12" cy="12" r="4"/></svg>
  </div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

function FitMarkers({ positions }) {
  const map = useMap();
  useEffect(() => {
    if (positions.length > 0) {
      const bounds = L.latLngBounds(positions);
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
    }
  }, [positions, map]);
  return null;
}

export default function JobMap({ job }) {
  const [techPos, setTechPos] = useState(null);
  const [drivingData, setDrivingData] = useState({ points: [], summary: {} });
  const [tracking, setTracking] = useState(false);
  const watchRef = useRef(null);
  const trackingRef = useRef(null);

  const customerLat = job.latitude;
  const customerLng = job.longitude;
  const hasCustomerLocation = customerLat && customerLng;

  // Get current GPS
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setTechPos({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {},
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
  }, []);

  // Fetch driving data
  const fetchDriving = useCallback(async () => {
    try {
      const { data } = await API.get(`/technicians/me/driving-data/${job.id}`);
      setDrivingData(data);
    } catch (err) { console.error(err); }
  }, [job.id]);

  useEffect(() => { fetchDriving(); }, [fetchDriving]);

  // Start/stop driving tracking
  const startTracking = () => {
    if (!navigator.geolocation) return;
    setTracking(true);

    watchRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        setTechPos({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      () => {},
      { enableHighAccuracy: true, maximumAge: 5000 }
    );

    // Log driving data every 10 seconds
    trackingRef.current = setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            await API.post(`/technicians/me/driving-data/${job.id}`, {
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
              speed: pos.coords.speed ? Math.round(pos.coords.speed * 2.237) : null, // m/s to mph
              heading: pos.coords.heading,
              timestamp: new Date().toISOString(),
            });
          } catch (e) { console.error(e); }
        },
        () => {},
        { enableHighAccuracy: true, timeout: 8000 }
      );
    }, 10000);
  };

  const stopTracking = () => {
    setTracking(false);
    if (watchRef.current !== null) {
      navigator.geolocation.clearWatch(watchRef.current);
      watchRef.current = null;
    }
    if (trackingRef.current) {
      clearInterval(trackingRef.current);
      trackingRef.current = null;
    }
    fetchDriving();
  };

  useEffect(() => {
    return () => {
      if (watchRef.current !== null) navigator.geolocation.clearWatch(watchRef.current);
      if (trackingRef.current) clearInterval(trackingRef.current);
    };
  }, []);

  const openDirections = () => {
    if (!hasCustomerLocation) return;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${customerLat},${customerLng}`;
    window.open(url, "_blank");
  };

  const mapCenter = hasCustomerLocation
    ? [customerLat, customerLng]
    : techPos ? [techPos.lat, techPos.lng] : [32.78, -96.80];

  const allPositions = [];
  if (hasCustomerLocation) allPositions.push([customerLat, customerLng]);
  if (techPos) allPositions.push([techPos.lat, techPos.lng]);

  const routePoints = drivingData.points
    .filter(p => p.latitude && p.longitude)
    .map(p => [p.latitude, p.longitude]);

  const summary = drivingData.summary || {};

  return (
    <div className="space-y-3" data-testid="job-map-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-red-500" />
          <h4 className="text-sm font-heading font-semibold text-zinc-900">Customer Location</h4>
        </div>
        {hasCustomerLocation && (
          <Button size="sm" variant="outline" onClick={openDirections}
            className="text-xs h-7 text-blue-600 border-blue-200 hover:bg-blue-50"
            data-testid="navigate-btn">
            <ExternalLink className="w-3 h-3 mr-1" /> Navigate
          </Button>
        )}
      </div>

      {/* Map */}
      <Card className="border border-zinc-200 overflow-hidden" data-testid="job-map-container">
        <div style={{ height: "200px", width: "100%" }}>
          <MapContainer center={mapCenter} zoom={13} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>' />
            {allPositions.length > 0 && <FitMarkers positions={allPositions} />}
            {hasCustomerLocation && (
              <Marker position={[customerLat, customerLng]} icon={customerIcon}>
                <Popup>
                  <div className="text-xs">
                    <p className="font-bold">{job.customer_name || "Customer"}</p>
                    <p>{job.property_address_line1}</p>
                    <p>{job.property_city}, {job.property_state}</p>
                  </div>
                </Popup>
              </Marker>
            )}
            {techPos && (
              <Marker position={[techPos.lat, techPos.lng]} icon={techIcon}>
                <Popup><span className="text-xs font-bold">Your Location</span></Popup>
              </Marker>
            )}
            {routePoints.length > 1 && (
              <Polyline positions={routePoints} color="#dc2626" weight={3} opacity={0.7} />
            )}
          </MapContainer>
        </div>
      </Card>

      {/* Address */}
      <div className="text-xs text-zinc-600 bg-zinc-50 rounded-lg p-2.5 flex items-start gap-2">
        <MapPin className="w-3.5 h-3.5 text-red-500 mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-medium">{job.property_address_line1}</p>
          <p className="text-zinc-400">{job.property_city}, {job.property_state} {job.property_zip_code}</p>
        </div>
      </div>

      {/* Driving Tracker */}
      <div className="flex gap-2">
        {!tracking ? (
          <Button size="sm" variant="outline" onClick={startTracking}
            className="flex-1 text-xs h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="start-tracking-btn">
            <Locate className="w-3.5 h-3.5 mr-1" /> Start Drive Tracking
          </Button>
        ) : (
          <Button size="sm" onClick={stopTracking}
            className="flex-1 text-xs h-8 bg-red-600 hover:bg-red-700 text-white"
            data-testid="stop-tracking-btn">
            <Navigation className="w-3.5 h-3.5 mr-1 animate-pulse" /> Stop Tracking
          </Button>
        )}
      </div>

      {/* Driving Summary */}
      {(summary.total_points > 0 || summary.total_distance_miles > 0) && (
        <div className="grid grid-cols-4 gap-1.5" data-testid="driving-summary">
          <div className="text-center p-2 bg-zinc-50 rounded-lg">
            <Route className="w-3.5 h-3.5 text-zinc-400 mx-auto mb-0.5" />
            <p className="text-xs font-bold text-zinc-900">{summary.total_distance_miles} mi</p>
            <p className="text-[9px] text-zinc-400">Distance</p>
          </div>
          <div className="text-center p-2 bg-zinc-50 rounded-lg">
            <Gauge className="w-3.5 h-3.5 text-zinc-400 mx-auto mb-0.5" />
            <p className="text-xs font-bold text-zinc-900">{summary.avg_speed_mph} mph</p>
            <p className="text-[9px] text-zinc-400">Avg Speed</p>
          </div>
          <div className="text-center p-2 bg-zinc-50 rounded-lg">
            <Gauge className="w-3.5 h-3.5 text-amber-500 mx-auto mb-0.5" />
            <p className="text-xs font-bold text-zinc-900">{summary.max_speed_mph} mph</p>
            <p className="text-[9px] text-zinc-400">Max Speed</p>
          </div>
          <div className="text-center p-2 bg-zinc-50 rounded-lg">
            <DollarSign className="w-3.5 h-3.5 text-red-500 mx-auto mb-0.5" />
            <p className="text-xs font-bold text-zinc-900">${summary.toll_expenses || 0}</p>
            <p className="text-[9px] text-zinc-400">Tolls</p>
          </div>
        </div>
      )}

      {/* Driving Safety Analytics */}
      {summary.total_points > 0 && summary.safety_score !== undefined && (
        <div className="border border-zinc-200 rounded-lg p-3 bg-white space-y-2"
          data-testid="driving-safety-block">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Shield className={`w-3.5 h-3.5 ${
                summary.safety_score >= 90 ? "text-emerald-500"
                : summary.safety_score >= 75 ? "text-blue-500"
                : summary.safety_score >= 60 ? "text-amber-500" : "text-red-500"
              }`} />
              <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">
                Driving Safety
              </span>
            </div>
            <Badge className={`text-[10px] border ${
              summary.risk_level === "Excellent" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : summary.risk_level === "Good" ? "bg-blue-50 text-blue-700 border-blue-200"
              : summary.risk_level === "Caution" ? "bg-amber-50 text-amber-700 border-amber-200"
              : "bg-red-50 text-red-700 border-red-200"
            }`}>
              {summary.risk_level} · {summary.safety_score}/100
            </Badge>
          </div>
          <div className="grid grid-cols-4 gap-1.5">
            <div className="text-center p-1.5 bg-red-50 rounded">
              <Gauge className="w-3 h-3 text-red-500 mx-auto" />
              <p className="text-xs font-bold text-red-700">{summary.speeding_samples || 0}</p>
              <p className="text-[8px] text-zinc-500">Speeding</p>
            </div>
            <div className="text-center p-1.5 bg-amber-50 rounded">
              <TrendingDown className="w-3 h-3 text-amber-600 mx-auto" />
              <p className="text-xs font-bold text-amber-700">{summary.harsh_brake_count || 0}</p>
              <p className="text-[8px] text-zinc-500">Harsh Brake</p>
            </div>
            <div className="text-center p-1.5 bg-amber-50 rounded">
              <Zap className="w-3 h-3 text-amber-600 mx-auto" />
              <p className="text-xs font-bold text-amber-700">{summary.rapid_accel_count || 0}</p>
              <p className="text-[8px] text-zinc-500">Rapid Accel</p>
            </div>
            <div className="text-center p-1.5 bg-red-50 rounded">
              <AlertTriangle className="w-3 h-3 text-red-500 mx-auto" />
              <p className="text-xs font-bold text-red-700">{summary.sharp_turn_count || 0}</p>
              <p className="text-[8px] text-zinc-500">Sharp Turn</p>
            </div>
          </div>
          {drivingData.events && drivingData.events.length > 0 && (
            <details className="border-t border-zinc-100 pt-2" data-testid="driving-events-details">
              <summary className="cursor-pointer text-[10px] font-bold text-zinc-600 uppercase tracking-wider hover:text-zinc-900">
                {drivingData.events.length} flagged event{drivingData.events.length === 1 ? "" : "s"} — view
              </summary>
              <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                {drivingData.events.slice(0, 20).map((e, i) => (
                  <li key={i} className="flex items-center justify-between text-[10px] bg-zinc-50 rounded px-2 py-1">
                    <span className="font-bold text-zinc-700">
                      {e.type === "harsh_brake" ? "Harsh Brake"
                       : e.type === "rapid_accel" ? "Rapid Accel"
                       : "Sharp Turn"}
                    </span>
                    <span className="text-zinc-500">
                      {e.type === "sharp_turn"
                        ? `${e.heading_change}° @ ${e.speed} mph`
                        : `${e.from_speed} → ${e.to_speed} mph`}
                    </span>
                    <span className="text-zinc-400">
                      {e.timestamp ? new Date(e.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
